/**
 * Sticky-top expandable music player. Feb 2026.
 *
 * Collapsed state shows: ▶/⏸ + current track title + ⏭ skip + chevron.
 * Expanded state reveals: the full song list (click any to jump to it)
 * + previous / next / volume controls.
 *
 * Mounted ONCE at the App root so it persists across route changes.
 * If the admin hasn't uploaded any songs yet, the player renders
 * nothing.
 */
import { useState } from "react";
import { useMusicPlayer } from "../context/MusicPlayerContext";

export default function MusicPlayer() {
  const {
    songs,
    loaded,
    index,
    currentSong,
    isPlaying,
    volume,
    toggle,
    next,
    prev,
    jumpTo,
    changeVolume,
  } = useMusicPlayer();
  const [expanded, setExpanded] = useState(false);

  // Don't render anything until the playlist has loaded — avoids a
  // jittery layout shift on first paint when songs is briefly [].
  if (!loaded || songs.length === 0) return null;

  return (
    <div
      className="rm-music-player"
      data-testid="music-player"
    >
      <div className="rm-music-player-bar">
        <button
          type="button"
          onClick={toggle}
          className="rm-music-btn rm-music-btn-primary"
          data-testid="music-player-toggle"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? "⏸" : "▶"}
        </button>
        <div className="rm-music-player-meta">
          <div
            className="rm-music-player-title"
            data-testid="music-player-title"
          >
            {currentSong?.title || "—"}
          </div>
          {currentSong?.artist && (
            <div className="rm-music-player-artist">
              {currentSong.artist}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={next}
          className="rm-music-btn"
          data-testid="music-player-next"
          aria-label="Next song"
          title="Next song"
        >
          ⏭
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="rm-music-btn rm-music-btn-chevron"
          data-testid="music-player-expand-toggle"
          aria-label={expanded ? "Collapse playlist" : "Expand playlist"}
          aria-expanded={expanded}
        >
          {expanded ? "▴" : "▾"}
        </button>
      </div>

      {expanded && (
        <div
          className="rm-music-player-panel"
          data-testid="music-player-panel"
        >
          <div className="rm-music-player-panel-controls">
            <button
              type="button"
              onClick={prev}
              className="rm-music-btn"
              data-testid="music-player-prev"
              aria-label="Previous song"
            >
              ⏮ Prev
            </button>
            <label className="rm-music-player-volume">
              <span aria-hidden="true">🔊</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => changeVolume(parseFloat(e.target.value))}
                data-testid="music-player-volume"
                aria-label="Volume"
              />
            </label>
            <button
              type="button"
              onClick={next}
              className="rm-music-btn"
              data-testid="music-player-next-bottom"
              aria-label="Next song"
            >
              Next ⏭
            </button>
          </div>
          <ul
            className="rm-music-player-list"
            data-testid="music-player-list"
          >
            {songs.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => jumpTo(i)}
                  className={`rm-music-player-track ${i === index ? "is-active" : ""}`}
                  data-testid={`music-player-track-${s.id}`}
                >
                  <span className="rm-music-player-track-num">{i + 1}.</span>
                  <span className="rm-music-player-track-title">
                    {s.title}
                  </span>
                  {s.artist && (
                    <span className="rm-music-player-track-artist">
                      {s.artist}
                    </span>
                  )}
                  {i === index && (
                    <span
                      className="rm-music-player-track-marker"
                      aria-hidden="true"
                    >
                      {isPlaying ? "♪" : "❚"}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
